import gql from 'graphql-tag';

/**
 * Builds the GraphQL query for searching users, with a single alias
 * for all users, to be used to perform simple searches on users.
 * E.g.: Rooms owner filter without a default value (from query string).
 */
const getSearchUsersQuery = (fields: string, fetchTotalCount?: boolean) => {
  const totalCount = fetchTotalCount ? 'totalCount' : '';

  return gql`
    query users(
      $first: Int,
      $status: String,
      $search: String,
      $searchFields: [String!]
    ) {
      allUsers: users(
        first: $first,
        status: $status,
        search: $search,
        searchFields: $searchFields
      ) {
        ${totalCount}
        nodes {
          ${fields}
        }
      }
    }
  `;
};

/**
 * Builds the GraphQL query for searching users, with an additional alias for
 * also fetching a set of users by their IDs, regardless of the original
 * `allUsers` alias which contains a fixed amount of users, based on the
 * pagination param.
 * E.g.: Rooms owner filter with a default owner value from a query string
 * param, which is necessary as the given ID could belong to a user that's
 * outside the pagination limit
 */
const getSearchUsersUnionByIdsQuery = (
  fields: string,
  fetchTotalCount?: boolean
) => {
  const totalCount = fetchTotalCount ? 'totalCount' : '';

  return gql`
    query users(
      $first: Int,
      $ids: [ID!],
      $status: String,
      $search: String,
      $searchFields: [String!]
    ) {
      allUsers: users(
        first: $first,
        status: $status,
        search: $search,
        searchFields: $searchFields
      ) {
        ${totalCount}
        nodes {
          ${fields}
        }
      }
      usersByIds: users (ids: $ids) {
        nodes {
          ${fields}
        }
      }
    }
  `;
};

export const searchUsersQuery = (
  fields: string,
  ids?: string[],
  fetchTotalCount?: boolean
) =>
  ids?.length
    ? getSearchUsersUnionByIdsQuery(fields, fetchTotalCount)
    : getSearchUsersQuery(fields, fetchTotalCount);
